import React, { useState, useEffect } from "react";
import { FaEdit } from "react-icons/fa";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Icon,
  Input,
  Select,
  Button,
  Box,
  Grid,
  useBreakpointValue,
  useToast,
} from "@chakra-ui/react";
import {
  FaPlus,
  FaUpload,
  FaCopy,
  FaTags,
  FaCalendarAlt,
  FaBarcode,
  FaBuilding,
  FaUserEdit,
  FaBook,
} from "react-icons/fa";
import { BASE_URL } from "../../../../Globalapi/Globalapi";
const Edit_BookList = ({ bookId, schoolId, userId }) => {
  const [open, setOpen] = useState(false); // State to handle modal visibility
  const [bookDetails, setBookDetails] = useState({
    name: "",
    author: "",
    publisher: "",
    isbn: "",
    location: "",
    copies: "",
    category: "", // Category ID for dropdown selection
    image: null,
    publication_year: new Date().getFullYear(), // Default to current year
  });
  const [categories, setCategories] = useState([]); // State to store categories from API
  const toast = useToast();
  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/admin/library/category/list_category.php?school_id=${schoolId}` // Replace `1` with dynamic school ID if needed
        );
        const data = await response.json();

        if (data.success) {
          setCategories(data.data); // Set categories from the API
        } else {
          console.error("Failed to fetch categories: ", data.message);
        }
      } catch (error) {
        console.error("Error fetching categories: ", error);
      }
    };

    fetchCategories();
  }, []);

  const handleOpen = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/admin/library/books/view_book.php?book_id=${bookId}`
      );
      const result = await response.json();

      if (result.success) {
        const { data } = result;
        // Set book details and pre-select category based on category_id
        setBookDetails({
          name: data.title,
          author: data.author,
          publisher: data.publisher,
          isbn: data.isbn,
          location: data.location,
          copies: data.available_copies,
          category: data.category_id, // Set category ID for pre-selection
          image: null, // Or set the image URL if available
          publication_year: data.publication_year,
        });
        setOpen(true); // Open the modal
      } else {
        toast({
          title: "Error fetching book details.",
          description: result.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch book details.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setBookDetails((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setBookDetails((prevDetails) => ({
          ...prevDetails,
          image: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    const payload = {
      user_id: sessionStorage.getItem("user_id"),
      school_id: sessionStorage.getItem("school_id"),
      book_id: bookId,
      title: bookDetails.name,
      author: bookDetails.author,
      isbn: bookDetails.isbn,
      publisher: bookDetails.publisher,
      publication_year: bookDetails.publication_year,
      total_copies: bookDetails.copies,
    };
    console.log("edit", payload);

    try {
      const response = await fetch(
        `${BASE_URL}/admin/library/books/edit_book.php?book_id=${bookId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();
      if (result.success) {
        toast({
          title: "Success",
          description: "Book updated successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        handleClose(); // Close the modal on success
      } else {
        toast({
          title: "Error",
          description: result.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update book details.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Responsive grid layout
  const gridTemplateColumns = useBreakpointValue({
    base: "1fr", // Single column for mobile
    md: "1fr 1fr", // Two columns for medium and up
  });

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 50 }, (_, index) => currentYear - index); // Generate last 50 years

  return (
    <div>
      {/* Button to open the edit modal */}
      <Button padding={0} size="sm" colorScheme="blue" onClick={handleOpen}>
        <FaEdit style={{ fontSize: "15px" }} />
      </Button>

      {/* Modal for viewing/editing book details */}
      <Modal
        isOpen={open}
        onClose={handleClose}
        size="lg"
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent
          borderRadius="lg"
          boxShadow="xl"
          bg="linear-gradient(135deg, #e0f7f7, #f0ffff)"
          color="gray.700"
          p={6}
          maxW="700px"
          maxH="550px"
          overflowY="auto"
        >
          <ModalHeader
            fontSize="xl"
            fontWeight="bold"
            textAlign="center"
            color="teal.600"
          >
            <Icon as={FaEdit} boxSize={5} mr={2} />
            Edit Book
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            overflowY="auto"
            px={4}
            css={{
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(173, 216, 230, 0.6)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(173, 216, 230, 0.9)",
              },
            }}
          >
            {/* Responsive grid layout */}
            <Grid templateColumns={gridTemplateColumns} gap={4} mb={4}>
              <FormControl id="name">
                <FormLabel>
                  {" "}
                  <Icon as={FaBook} mr={1} color="teal.500" />
                  Book Name
                </FormLabel>
                <Input
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  _focus={{
                    borderColor: "teal.500",
                    boxShadow: "0 0 0 1px teal.500",
                  }}
                  type="text"
                  name="name"
                  value={bookDetails.name}
                  onChange={handleChange}
                />
              </FormControl>

              <FormControl id="author">
                <FormLabel>
                  {" "}
                  <Icon as={FaUserEdit} mr={1} color="teal.500" />
                  Author
                </FormLabel>
                <Input
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  _focus={{
                    borderColor: "teal.500",
                    boxShadow: "0 0 0 1px teal.500",
                  }}
                  type="text"
                  name="author"
                  value={bookDetails.author}
                  onChange={handleChange}
                />
              </FormControl>

              <FormControl id="publisher">
                <FormLabel>
                  {" "}
                  <Icon as={FaBuilding} mr={1} color="teal.500" />
                  Publisher
                </FormLabel>
                <Input
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  _focus={{
                    borderColor: "teal.500",
                    boxShadow: "0 0 0 1px teal.500",
                  }}
                  type="text"
                  name="publisher"
                  value={bookDetails.publisher}
                  onChange={handleChange}
                />
              </FormControl>

              <FormControl id="isbn">
                <FormLabel>
                  {" "}
                  <Icon as={FaBarcode} mr={1} color="teal.500" />
                  ISBN
                </FormLabel>
                <Input
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  _focus={{
                    borderColor: "teal.500",
                    boxShadow: "0 0 0 1px teal.500",
                  }}
                  type="text"
                  name="isbn"
                  value={bookDetails.isbn}
                  onChange={handleChange}
                />
              </FormControl>

              <FormControl id="location">
                <FormLabel>
                  {" "}
                  <Icon as={FaCalendarAlt} mr={1} color="teal.500" />
                  Location
                </FormLabel>
                <Input
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  _focus={{
                    borderColor: "teal.500",
                    boxShadow: "0 0 0 1px teal.500",
                  }}
                  type="text"
                  name="location"
                  value={bookDetails.location}
                  onChange={handleChange}
                />
              </FormControl>

              <FormControl id="copies">
                <FormLabel>
                  {" "}
                  <Icon as={FaCopy} mr={1} color="teal.500" />
                  Number of Copies
                </FormLabel>
                <Input
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  _focus={{
                    borderColor: "teal.500",
                    boxShadow: "0 0 0 1px teal.500",
                  }}
                  type="number"
                  name="copies"
                  value={bookDetails.copies}
                  onChange={handleChange}
                />
              </FormControl>

              {/* Category dropdown */}
              <FormControl id="category">
                <FormLabel>
                  {" "}
                  <Icon as={FaTags} mr={1} color="teal.500" />
                  Category
                </FormLabel>
                <Select
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  _focus={{
                    borderColor: "teal.500",
                    boxShadow: "0 0 0 1px teal.500",
                  }}
                  name="category"
                  value={bookDetails.category} // Pre-select category based on category_id
                  onChange={handleChange}
                  placeholder="Select Category"
                >
                  {categories.length > 0 ? (
                    categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))
                  ) : (
                    <option>Loading categories...</option>
                  )}
                </Select>
              </FormControl>

              {/* Publication Year dropdown */}
              <FormControl id="publication_year">
                <FormLabel>
                  {" "}
                  <Icon as={FaCalendarAlt} mr={1} color="teal.500" />
                  Publication Year
                </FormLabel>
                <Select
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  _focus={{
                    borderColor: "teal.500",
                    boxShadow: "0 0 0 1px teal.500",
                  }}
                  name="publication_year"
                  value={bookDetails.publication_year}
                  onChange={handleChange}
                  placeholder="Select Year"
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl id="image">
                <FormLabel>
                  {" "}
                  <Icon as={FaUpload} mr={1} color="teal.500" />
                  Upload Book Image
                </FormLabel>
                <Input
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  _focus={{
                    borderColor: "teal.500",
                    boxShadow: "0 0 0 1px teal.500",
                  }}
                  type="file"
                  onChange={handleImageChange}
                  accept="image/*"
                />
                {bookDetails.image && (
                  <Box mt={2}>
                    <img
                      src={bookDetails.image}
                      alt="Book Cover"
                      style={{ width: "100px", height: "auto" }}
                    />
                  </Box>
                )}
              </FormControl>
            </Grid>
          </ModalBody>

          <ModalFooter>
            {/* <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button> */}
            <Button
              bgGradient="linear(to-r, teal.400, teal.600)"
              color="white"
              _hover={{ bgGradient: "linear(to-r, teal.500, teal.700)" }}
              onClick={handleSubmit}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default Edit_BookList;
